import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Pill } from "@phosphor-icons/react";

export default function Register() {
  const { register, error } = useAuth();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const redirectTo = params.get("redirect");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await register(email, password, name);
    setBusy(false);
    if (ok) navigate(redirectTo || "/app/patients?new=1");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFBF7]">
      <div className="flex items-center justify-center px-6 py-12 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-full bg-[#2C5545] flex items-center justify-center">
              <Pill weight="duotone" className="text-white" size={22} />
            </div>
            <div className="font-heading font-extrabold text-xl">EverCara</div>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Create your account</h1>
          <p className="text-[#576A62] mt-2">Start caring in minutes.</p>

          <div className="mt-8 space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2">Your name</label>
              <input
                data-testid="register-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full h-12 px-4 rounded-xl border border-[#EBE8E0] bg-white focus:ring-2 focus:ring-[#2C5545] outline-none"
                placeholder="Jamie Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 px-4 rounded-xl border border-[#EBE8E0] bg-white focus:ring-2 focus:ring-[#2C5545] outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                data-testid="register-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 px-4 rounded-xl border border-[#EBE8E0] bg-white focus:ring-2 focus:ring-[#2C5545] outline-none"
                placeholder="At least 6 characters"
              />
            </div>
            {error && (
              <div className="text-sm text-[#C86A51] bg-[#C86A51]/10 rounded-lg px-4 py-3" data-testid="register-error">
                {error}
              </div>
            )}
            <button
              data-testid="register-submit"
              disabled={busy}
              className="w-full h-12 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white font-semibold transition-colors disabled:opacity-60"
            >
              {busy ? "Creating…" : "Create account"}
            </button>
          </div>
          <div className="mt-6 text-sm text-[#576A62]">
            Already have an account?{" "}
            <Link to="/login" data-testid="link-to-login" className="text-[#2C5545] font-semibold hover:underline">
              Sign in
            </Link>
          </div>
        </form>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img
          src="https://images.pexels.com/photos/7345443/pexels-photo-7345443.jpeg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tl from-[#1A2520]/60 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.2em] opacity-80">EverCara</div>
          <div className="mt-2 font-heading text-3xl font-bold leading-tight">
            One dashboard for the people you love.
          </div>
        </div>
      </div>
    </div>
  );
}
